import { useGetCurrentUserQuery } from '@carboncopyco/data';
import {
  PlusOutlineIcon,
  RightOutlineIcon,
  UpdatedOutlineIcon,
} from '@carboncopyco/icons';
import {
  EmailIllustration,
  GetToKnowDemostackIllustration,
  TrendingIllustration,
} from '@carboncopyco/illustrations';
import { TopAssetsSorts } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  DropdownFilter,
  Logo,
  TextSectionHeader,
  Typography,
} from '@carboncopyco/ui';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { periodOptions } from '../Charts/consts';
import type { TimePeriod } from '../InfoPage/PlaybookPage/types';
import { IntegrationErrorBanner } from '../IntegrationErrorBanner';
import { LEARNING_HUB_CONSOLE } from '../consts';
import { type ConsolePageProps, ConsoleProvider } from './ConsoleContext';
import { ConsoleCreateNewMenu } from './ConsoleCreateNewMenu';
import { DemoAnalyticsCard } from './DemoAnalyticsCard';
import { FeatureCard } from './FeatureCard';
import { LearningHubCard } from './LearningHubCard';
import { MyTopAssetsTable } from './MyTopAssetsTable';
import { PresentationQueue } from './PresentationQueue';
import { TeamTopAssetsTable } from './TeamTopAssetsTable';
import { VisitsAnalyticsCard } from './VisitAnalyticsCard';
import { TableControlsProvider } from './useTopAssetsTableControls';

export const ConsolePage = (props: ConsolePageProps) => {
  const [period, setPeriod] = useState<number>(30);
  const { data: userData } = useGetCurrentUserQuery();
  const {
    goToNotificationSettings,
    goToAnalyticsDashboard,
    goToIntegrationSettings,
    goToAssetPage,
    goToRecorder,
    createTourFromLiveApp,
    createTourFromDemo,
    createOverlay,
    goToTeamActivity,
  } = props;
  const { organizationId } = useParams();
  const userRole = userData?.user?.roles?.[organizationId];

  return (
    <ConsoleProvider
      {...props}
      period={period}
      setPeriod={setPeriod}
    >
      <div className="relative h-full">
        <div className="absolute z-20 left-0 top-0 right-0">
          <IntegrationErrorBanner goToIntegrations={goToIntegrationSettings} />
        </div>

        <div className="bg-ds-natural-50 h-full w-full flex justify-center items-start py-12">
          <div className="flex flex-col w-ds-page gap-5">
            <div className="flex items-center justify-between">
              <Typography
                color="text-ds-natural-700"
                label={`Welcome, ${userData?.user?.firstName}`}
                tagName="h1"
                fontSize="text-ds-800"
                lineHeight="leading-ds-800"
                fontWeight="font-ds-semibold"
              />
              <div className="flex items-center gap-5">
                <DropdownFilter
                  size="medium"
                  rounded={false}
                  capsuleWidth={168}
                  contentWidth={168}
                  placeholder=""
                  selected={period.toString() as TimePeriod}
                  items={periodOptions}
                  onSelect={(periodString: TimePeriod) => {
                    setPeriod(Number(periodString));
                  }}
                />
                <Button
                  onClick={goToTeamActivity}
                  secondary
                  icon={<UpdatedOutlineIcon />}
                />
                {userRole !== 'presenter' && (
                  <ConsoleCreateNewMenu
                    asChild
                    triggerElement={
                      <Button
                        leftIcon={<PlusOutlineIcon />}
                        label="New"
                      />
                    }
                    navigateToRecorder={goToRecorder}
                    navigateToTourCapture={createTourFromLiveApp}
                    goToDemo={(id, workspaceId) => {
                      goToAssetPage({
                        id: String(id),
                        workspaceId,
                        type: 'demo',
                      });
                    }}
                    navigateToTourFromDemo={createTourFromDemo}
                    handleOpenRecorder={goToRecorder}
                    goToCreateOverlayDemoPage={createOverlay}
                    goToPlaybook={(id, workspaceId) =>
                      goToAssetPage({
                        id,
                        workspaceId,
                        type: 'playbook',
                      })
                    }
                  />
                )}
              </div>
            </div>
            <div className="grid gap-5 grid-cols-3 auto-rows-[176px_1100px]">
              <div className="rounded-md bg-ds-primary-200 py-3 px-4 relative">
                <div className="h-full flex flex-col items-start justify-between">
                  <div>
                    <TextSectionHeader
                      label="Get to know"
                      color="text-ds-accent3-100"
                    />
                    <div className="pt-2">
                      <Logo color={ColorHex.accent3100} />
                    </div>
                  </div>
                  <Button
                    className="pb-3 text-ds-accent3"
                    rightIcon={<RightOutlineIcon />}
                    linkDark
                    label="Start now"
                    onClick={() => {
                      window.open(LEARNING_HUB_CONSOLE);
                    }}
                  />
                </div>
                <div className="absolute bottom-0 right-4">
                  <GetToKnowDemostackIllustration />
                </div>
              </div>
              <DemoAnalyticsCard />
              <VisitsAnalyticsCard />
              <div className="col-span-2 gap-5 flex flex-col justify-between">
                <TableControlsProvider
                  defaultSort={TopAssetsSorts.last_updated}
                >
                  <MyTopAssetsTable />
                </TableControlsProvider>

                <TableControlsProvider defaultSort={TopAssetsSorts.top_results}>
                  <TeamTopAssetsTable />
                </TableControlsProvider>
              </div>
              <div className="flex flex-col justify-between gap-5">
                <div className="overflow-hidden grow min-h-[200px]">
                  <PresentationQueue />
                </div>
                <LearningHubCard />
                <div className="flex flex-col gap-5">
                  <FeatureCard
                    title="Analytics Dashboard"
                    description={
                      <>
                        Get a complete understanding of <br /> deal impact and
                        team performance.
                      </>
                    }
                    illustration={<TrendingIllustration />}
                    goToFeaturePage={goToAnalyticsDashboard}
                    badgeLabel="trending"
                    badgeVariant="red"
                  />
                  <FeatureCard
                    title="Email Notification"
                    description={
                      <>
                        Drive deal momentum by staying on <br /> top of things
                        in real time.
                      </>
                    }
                    illustration={<EmailIllustration />}
                    goToFeaturePage={goToNotificationSettings}
                    badgeLabel="new"
                    badgeVariant="yellow"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ConsoleProvider>
  );
};
